import { NgModule, ErrorHandler, LOCALE_ID, provideZoneChangeDetection } from '@angular/core';
import { CommonModule, registerLocaleData } from '@angular/common';
import { BrowserModule } from '@angular/platform-browser';
import { FormsModule } from '@angular/forms';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi, withXsrfConfiguration } from '@angular/common/http';
import { RouterModule, PreloadAllModules } from '@angular/router';
import { TranslateModule, TranslateLoader } from '@ngx-translate/core';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';

/*
 * Platform and Environment providers/directives/pipes
 */
import { environment } from 'environments/environment';
import { ROUTES } from './app.routes';
import { APP_RESOLVER_PROVIDERS } from './app.resolver';
/*
 * Components
 */
// App is our top level component
import { App } from './components/main/app.component';

/*
 * Services
 */
import { AppState, InternalStateType } from './services/app.service';
import { CustomErrorHandler } from './services/customErrorHandler.service';
import { CustomHttpInterceptor } from './services/customHttpInterceptor.service';
import { ApiGatewayHelperService } from './services/apigateway-helper.service';
import { AuthService } from './services/auth.service';
import { GigyaSessionService } from './services/gigya-session.service';
import { AuthenticatedRoutesGuard } from './services/authenticated-routes-guard.service';
import { CustomTranslationLoader } from './services/custom-translation-loader';
import { LoaderService } from './services/loader.service';
import { CacheService } from './services/cache.service';
import { CustomBackendLoggerService } from './services/custom-backend-logger.service';
import { SharedAllModule } from '../shared-all/shared-all.module';
import { AppHelperService } from './services/app-helper.service';
import { isUserAuthentcatedRoutesGuard } from './services/isUserAuthentcated-routes-guard.service';
import { RoleBasedRouting } from './components/main/roleBasedRouting.component';
import { ExternalRoutesGuard } from './services/external-routes-guard.service';
import { HtmlMetaManagerService } from './services/html-meta-manager.service';
import { PageTitleService } from './services/page-title.service';

import { appSetting } from 'config/appSettings';
import localeEs from '@angular/common/locales/es';
import { LearnerModule } from './../learner/learner.module';
import { TeacherModule } from './../teacher/teacher.module';

import cavClient from 'libs-cavalier-integration/frontend';
import { UserDataService } from './services/user-data.service';


let selectedLocale = 'en';
const loggedInUserRole = localStorage.getItem(appSetting.localStorage.loggedInUserRole);
if (['teacher', 'admin', 'superadmin'].indexOf(loggedInUserRole) == -1) {
  selectedLocale = localStorage.getItem(appSetting.localStorage.lang) || 'en';
}

switch(selectedLocale){
  case 'es': {
    registerLocaleData(localeEs);
    break;
  }
}

// initialize cavalier client
cavClient.init('learning-path')
cavClient.capturePageViewEvents();

// Application wide providers
const APP_PROVIDERS = [
  ...APP_RESOLVER_PROVIDERS,
  AppState
];

type StoreType = {
  state: InternalStateType,
  restoreInputValues: () => void,
  disposeOldHosts: () => void
};


/**
 * `AppModule` is the main entry point into Angular2's bootstraping process
 */
@NgModule({
  bootstrap: [ App ],
  declarations: [
    App,
    RoleBasedRouting
  ],
  /**
   * Import Angular's modules.
   */
  imports: [
    BrowserModule,
    CommonModule,
    BrowserAnimationsModule,
    FormsModule,
    SharedAllModule,
    RouterModule.forRoot(ROUTES, {
    preloadingStrategy: PreloadAllModules
}),
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader, 
        useClass: CustomTranslationLoader
      }
    }),
    LearnerModule,
    TeacherModule
  ],
  /**
   * Expose our Services and Providers into Angular's dependency injection.
   */
  providers: [
    environment.ENV_PROVIDERS,
    APP_PROVIDERS,
    { provide: ErrorHandler, useClass: CustomErrorHandler },
    { provide: HTTP_INTERCEPTORS, useClass: CustomHttpInterceptor, multi: true } ,
    AuthService,
    ApiGatewayHelperService,
    GigyaSessionService,
    CustomTranslationLoader,
    AuthenticatedRoutesGuard,
    LoaderService,
    CacheService,
    CustomBackendLoggerService,
    AppHelperService,
    isUserAuthentcatedRoutesGuard,
    ExternalRoutesGuard,
    UserDataService,
    provideZoneChangeDetection(),
    {
      provide: LOCALE_ID,
      useValue: selectedLocale 
    },
    HtmlMetaManagerService,
    provideHttpClient(withInterceptorsFromDi(), withXsrfConfiguration({
      cookieName: 'csrf-token',
      headerName: 'csrf-token',
    })),
    PageTitleService
  ]
})
export class AppModule {}
